import debounce from 'lodash.debounce';
import { ChangeEvent, KeyboardEvent, ReactElement, useCallback, useMemo, useState } from 'react';
import { ListItem } from 'reactApp/ui/DropdownList/DropdownList';
import { TUseCallbacks } from 'reactApp/ui/InputSuggest/InputSuggest.types';

export const useCallbacks: TUseCallbacks = ({
    suggestions,
    onChange,
    getSuggestionItemId,
    inputRef,
    renderSuggestionItem,
    onRequestSuggestions,
    disabled,
}) => {
    const [selectedSuggestionId, setSelectedSuggestionId] = useState<number>(-1);
    const [shouldDropdownClose, setDropdownClose] = useState<boolean>(true);

    const onItemClick = useCallback(
        (id: string) => {
            onChange(null, { value: id });
        },
        [onChange]
    );

    const list: ListItem[] = useMemo(
        () =>
            suggestions?.map((item, index) => ({
                id: getSuggestionItemId(item),
                onClick: onItemClick,
                isSelected: index === selectedSuggestionId,
                ...item,
            })) || [],
        [getSuggestionItemId, onItemClick, selectedSuggestionId, suggestions]
    );

    const showDropdown = Boolean(list?.length) && !shouldDropdownClose && !disabled;

    const handleInputKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (!list?.length) {
                return;
            }

            switch (event.key) {
                case 'ArrowUp':
                    if (selectedSuggestionId >= 0) {
                        setSelectedSuggestionId(selectedSuggestionId - 1);
                    }
                    event.preventDefault();
                    break;
                case 'ArrowDown':
                    if (selectedSuggestionId < list.length - 1) {
                        setSelectedSuggestionId(selectedSuggestionId + 1);
                    }
                    event.preventDefault();
                    break;
                case 'Enter':
                    if (selectedSuggestionId < list.length && selectedSuggestionId >= 0) {
                        onChange(null, { value: list[selectedSuggestionId].id });
                        setDropdownClose(true);
                        event.preventDefault();
                    }
                    break;
                default:
                    setSelectedSuggestionId(-1);
                    break;
            }
        },
        [list, onChange, selectedSuggestionId]
    );

    const renderItem = useCallback(
        (item: ListItem): ReactElement | null => {
            if (!inputRef?.current) {
                return null;
            }

            const { width } = inputRef.current.getBoundingClientRect();

            // из ширины item вычитаем паддинг самого дропдауна
            return renderSuggestionItem(item, width - 2 - 16 * 2);
        },
        [renderSuggestionItem]
    );

    const calcDropdownPosition = useCallback(() => {
        if (!inputRef?.current) {
            return { posX: 0, posY: 0 };
        }

        const { top, left, height } = inputRef.current.getBoundingClientRect();

        return {
            posY: top + height + window.scrollY,
            posX: left,
        };
    }, []);

    const requestSuggestions = debounce((value) => onRequestSuggestions(value), 250);

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const { value } = event.target || {};

            setDropdownClose(false);
            onChange(event, { value });
            requestSuggestions(value);
        },
        [onChange, requestSuggestions]
    );

    const closeDropdown = () => setDropdownClose(true);

    return { handleInputKeyDown, closeDropdown, handleChange, calcDropdownPosition, renderItem, showDropdown, list };
};
